import { useState, type ReactNode } from 'react'
import { QrCode, Search } from 'lucide-react'
import { ApiError, api } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import { qrMissIsOtherPlant } from '@/lib/plantAccess'
import { byUrgency, searchMachines } from '@/lib/fiveSBoard'
import { QrScanner, tokenFromScan } from '@/components/QrScanner'
import { ScoreChip, StatusChip } from '@/components/FiveSBoard'
import { Button, Card, Input, Modal, Spinner } from '@/components/ui'
import { useT } from '@/i18n/I18nProvider'
import { cn } from '@/lib/utils'
import type { FiveSBoardMachine, WorkCenter } from '@/types/api'

/**
 * Which machine a walk is of: a searchable list with the ones still due
 * first, or the QR sticker on the machine itself.
 *
 * It replaced a `<select>` of every machine in alphabetical order, which on a
 * phone in front of the machine meant scrolling a native wheel past the ones
 * already walked to find the one in front of you — with nothing in it to say
 * which those were. The rows come from the month's board, so the picker and
 * `/5s` agree about what is still due.
 */
export function FiveSMachinePicker({
  machines,
  loading,
  onPick,
  onContinue,
}: {
  machines: FiveSBoardMachine[]
  loading: boolean
  onPick: (id: string) => void
  /** A machine somebody already started this month: open that walk instead. */
  onContinue: (evaluationId: string) => void
}) {
  const t = useT()
  const [search, setSearch] = useState('')

  const found = byUrgency(searchMachines(machines, search))
  const due = found.filter((machine) => machine.status === 'due' || machine.status === 'draft')
  const walked = found.filter((machine) => machine.status === 'walked')
  const blocked = found.filter((machine) => machine.status === 'noTemplate')

  return (
    <Card className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold">{t('fiveS.picker.title')}</h2>
        <MachineScanButton onFound={(centre) => onPick(centre.id)} />
      </div>

      <div className="relative">
        <Search
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
          aria-hidden
        />
        <Input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('fiveS.picker.search')}
          aria-label={t('fiveS.picker.search')}
          className="pl-9"
        />
      </div>

      {loading && <Spinner label={t('state.loading')} />}

      {!loading && found.length === 0 && (
        <p className="muted py-4 text-center text-sm">{t('fiveS.picker.none')}</p>
      )}

      <PickerSection title={t('fiveS.picker.due')} count={due.length}>
        {due.map((machine) => (
          <PickerRow
            key={machine.id}
            machine={machine}
            onClick={() =>
              machine.status === 'draft' && machine.draft
                ? onContinue(machine.draft.id)
                : onPick(machine.id)
            }
          />
        ))}
      </PickerSection>

      <PickerSection title={t('fiveS.picker.walked')} count={walked.length}>
        {walked.map((machine) => (
          <PickerRow key={machine.id} machine={machine} onClick={() => onPick(machine.id)} />
        ))}
      </PickerSection>

      <PickerSection title={t('fiveS.picker.noTemplate')} count={blocked.length}>
        {blocked.map((machine) => (
          <PickerRow key={machine.id} machine={machine} />
        ))}
      </PickerSection>
    </Card>
  )
}

function PickerSection({
  title,
  count,
  children,
}: {
  title: string
  count: number
  children: ReactNode
}) {
  if (count === 0) return null

  return (
    <section className="space-y-1.5">
      <h3 className="muted text-xs font-medium tracking-wide uppercase">
        {title} <span className="tabular-nums">({count})</span>
      </h3>
      <ul className="divide-y divide-[var(--border-subtle)] overflow-hidden rounded-xl border border-[var(--border-subtle)]">
        {children}
      </ul>
    </section>
  )
}

/**
 * One machine, as a single tap target the width of the screen.
 *
 * No `onClick` means it cannot be walked — a machine with no checklist — and
 * the row is shown, disabled, so its absence from the rest is explained.
 */
function PickerRow({ machine, onClick }: { machine: FiveSBoardMachine; onClick?: () => void }) {
  const t = useT()
  const score = machine.walk ?? machine.last

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        disabled={!onClick}
        className={cn(
          'min-h-control flex w-full items-center gap-3 px-3 py-row text-left transition-colors',
          'hover:bg-muted/60 focus-visible:bg-muted/60 disabled:cursor-not-allowed disabled:opacity-60',
        )}
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">
            {machine.name}
            {machine.isMine && (
              <span className="muted ml-1.5 text-xs font-normal">· {t('fiveS.board.mine')}</span>
            )}
          </span>
          <span className="muted block truncate text-xs">
            {[machine.plant?.name, machine.code].filter(Boolean).join(' · ')}
            {machine.status === 'draft' && ` · ${t('fiveS.picker.continueDraft')}`}
            {machine.status !== 'draft' && score && ` · ${'evaluatedOnLabel' in score ? score.evaluatedOnLabel : score.label}`}
          </span>
        </span>

        {/* The last score, so the evaluator knows what they are walking up to. */}
        {score && <ScoreChip value={score.scorePercentage} />}
        {machine.status !== 'walked' && <StatusChip status={machine.status} />}
      </button>
    </li>
  )
}

/**
 * Read the sticker on a machine, and say which machine it is.
 *
 * The sticker holds an opaque token — see `/qr/{token}` — so the answer has to
 * come from the server, which is also what decides whether the reader may see
 * that machine at all.
 */
export function MachineScanButton({
  onFound,
  variant = 'secondary',
}: {
  onFound: (centre: WorkCenter) => void
  variant?: 'primary' | 'secondary'
}) {
  const t = useT()
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /*
   * Bumped to start the camera again after a miss. The scanner reports one
   * code and stops — which is right, a second read of the same sticker would
   * resolve it twice — so "try again" is a fresh scanner.
   */
  const [attempt, setAttempt] = useState(0)

  function close() {
    setOpen(false)
    setError(null)
    setBusy(false)
  }

  async function resolve(text: string) {
    setBusy(true)

    try {
      const { data } = await api.get<{ data: WorkCenter }>(`/qr/${tokenFromScan(text)}`)
      close()
      onFound(data.data)
    } catch (failure) {
      // See `qrMissIsOtherPlant`: the same 404, the likelier reason.
      const status = failure instanceof ApiError ? failure.status : undefined

      setError(qrMissIsOtherPlant(status, user) ? t('tasks.qr.otherPlant') : t('tasks.scan.unknownCode'))
      setBusy(false)
    }
  }

  return (
    <>
      <Button variant={variant} onClick={() => setOpen(true)}>
        <QrCode className="size-4" aria-hidden />
        {t('fiveS.scan.button')}
      </Button>

      <Modal open={open} onClose={close} title={t('fiveS.scan.title')} description={t('fiveS.scan.hint')}>
        {error ? (
          <div className="space-y-3 py-2 text-center">
            <p className="text-sm">{error}</p>
            <Button
              variant="secondary"
              onClick={() => {
                setError(null)
                setAttempt((n) => n + 1)
              }}
            >
              {t('fiveS.scan.again')}
            </Button>
          </div>
        ) : busy ? (
          <Spinner label={t('fiveS.scan.opening')} />
        ) : (
          // Mounted only while the dialog is open, so the camera is released
          // the moment it closes — see QrScanner.
          open && (
            <QrScanner
              key={attempt}
              onResult={(text) => void resolve(text)}
              onError={(message) => setError(message)}
              className="mx-auto max-w-sm"
            />
          )
        )}
      </Modal>
    </>
  )
}
